/* global window, document */

import videojs from 'video.js';

/**
 * Class representing Google DAI Tracker.
 */
class DAITracker {
  /**
   * Create a DAITracker instance.
   */
  constructor(encore) {
    this.encore = encore;
    this.clickElement = document.getElementById('adClickThru');
    this.streamManager = null;
  }

  /**
   * Stream load handler. Sets live stream as source.
   */
  loadStreamHandler(event) {
    const streamUrl = event.getStreamData().url;

    if (!streamUrl) {
      videojs.log.error('DAI: stream data empty');
      return;
    }

    this.encore.source.setLiveStreamSource(event.getStreamData().url);
    videojs.log(`DAI: loaded stream [${event.getStreamData().url}]`);
  }

  /**
   * Stream load error handler.
   */
  loadStreamErrorHandler() {
    window.HRST_DAI_READY = true;
    videojs.log('DAI: could not load stream');
  }

  /**
   * Ad break started handler. Shows learn more button.
   */
  adBreakStartHandler() {
    this.clickElement.classList.remove('hidden');
    videojs.log('DAI: ad break started');
  }

  /**
   * Ad break ended handler. Hides learn more button.
   */
  adBreakEndHandler() {
    this.clickElement.classList.add('hidden');
    videojs.log('DAI: ad break ended');
  }

  /**
   * Click handler. Pauses video.
   */
  clickHandler() {
    this.encore.player.pause();
    videojs.log('DAI: click captured');
  }

  /**
   * Set up stream manager and register ad break and click events.
   */
  setupStreamManager() {
    this.streamManager = new window.google.ima.dai.api.StreamManager(
      this.encore.player.el().children[0]);

    this.streamManager.setClickElement(this.clickElement);

    this.streamManager.addEventListener(
      window.google.ima.dai.api.StreamEvent.Type.AD_BREAK_STARTED,
      this.adBreakStartHandler.bind(this), false);

    this.streamManager.addEventListener(
      window.google.ima.dai.api.StreamEvent.Type.AD_BREAK_ENDED,
      this.adBreakEndHandler.bind(this), false);

    this.streamManager.addEventListener(
      window.google.ima.dai.api.StreamEvent.Type.CLICK,
      this.clickHandler.bind(this), false);
  }

  /**
   * Set up track events to update ID3 tags on cue changes.
   */
  setupCueEvents() {
    this.encore.player.textTracks().addEventListener('addtrack', addTrackEvent => {
      const { track } = addTrackEvent;
      track.mode = 'hidden';

      track.addEventListener('cuechange', () => {
        const activeCue = track.activeCues[0];
        if (activeCue && activeCue.value && activeCue.value.data && activeCue.value.key) {
          const daiMeta = {};

          daiMeta[activeCue.value.key] = activeCue.value.data;
          daiMeta.width = Math.floor(this.encore.player.currentWidth());
          daiMeta.height = Math.floor(this.encore.player.currentHeight());
          daiMeta.duration = Number.POSITIVE_INFINITY;

          if (this.streamManager) {
            this.streamManager.onTimedMetadata(daiMeta);
          }
        }
      });
    });
  }

  /**
   * Registers stream events and requests a live stream.
   * @returns {Promise}
   */
  requestLiveStream() {
    const requestPromise = new Promise(resolve => {
      this.streamManager.addEventListener(window.google.ima.dai.api.StreamEvent.Type.LOADED,
        event => {
          this.loadStreamHandler(event);
          resolve();
        }, false);
      this.streamManager.addEventListener(window.google.ima.dai.api.StreamEvent.Type.ERROR,
        () => {
          this.loadStreamErrorHandler();
          resolve();
        }, false);
    });

    const streamRequest = new window.google.ima.dai.api.LiveStreamRequest();
    streamRequest.assetKey = this.encore.source.daiAssetId;
    streamRequest.apiKey = '';
    streamRequest.attemptPreroll = false;

    if (this.encore.settings.doNotSellStatus) {
      streamRequest.adTagParameters = { rdp: 1 };
      if (window.HTV_DAI_RDP_CUST) {
        streamRequest.adTagParameters.cust_params = encodeURI(window.HTV_DAI_RDP_CUST);
      }
    }

    this.streamManager.requestStream(streamRequest);

    return requestPromise;
  }
}

export default DAITracker;
